import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cn } from "@/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"

const badgeVariants = cva(
  "inline-flex items-center justify-center gap-1 rounded-2xl font-medium text-center transition-colors focus:outline-none",
  {
    variants: {
      variant: {
        // Base color variants
        beige: "bg-[#FCEBDF] text-[#242424]",
        pink: "bg-[#FEDBD7] text-[#242424]",
        orange: "bg-[#FCD4C9] text-[#242424]",
        electricGreen: "bg-[#E3FF6A] text-[#242424]",
        blue: "bg-[#DCE2FC] text-[#242424]",
        green: "bg-[#CCE0D9] text-[#242424]",
        teal: "bg-[#BAE7E8] text-[#242424]",
        lavender: "bg-[#E9D6EE] text-[#242424]",
        
        // Light variants
        beigeLight: "bg-[#FAF4F0] text-[#242424]",
        pinkLight: "bg-[#FFEEED] text-[#242424]",
        orangeLight: "bg-[#FFE8E1] text-[#242424]",
        electricGreenLight: "bg-[#F4FFC6] text-[#242424]",
        blueLight: "bg-[#EDF0FF] text-[#242424]",
        greenLight: "bg-[#EAF4F1] text-[#242424]",
        tealLight: "bg-[#DCF4F5] text-[#242424]",
        lavenderLight: "bg-[#FAF0FC] text-[#242424]",
        
        // Dark variants
        beigeDark: "bg-[#D4C3BB] text-[#242424]",
        pinkDark: "bg-[#FF9C94] text-[#242424]",
        orangeDark: "bg-[#F2B6A4] text-[#242424]",
        electricGreenDark: "bg-[#DAF565] text-[#242424]",
        blueDark: "bg-[#A4B1EB] text-[#242424]",
        greenDark: "bg-[#A5C9BA] text-[#242424]",
        tealDark: "bg-[#7AC9CC] text-[#242424]",
        lavenderDark: "bg-[#D6B4DE] text-[#242424]",
        
        // Grey variants
        greyLight: "bg-[#F7F4F2] text-[#242424]",
        grey: "bg-[#E5E1DF] text-[#242424]",
        greyDark: "bg-[#CCC8C6] text-[#242424]",
        
        // Utility variants
        success: "bg-[#009735] text-[#FAF4F0]",
        successMedium: "bg-[#27B85A] text-[#FAF4F0]",
        successDark: "bg-[#006623] text-[#FAF4F0]",
        warning: "bg-[#BD5601] text-[#FAF4F0]",
        warningMedium: "bg-[#EC7303] text-[#FAF4F0]",
        warningDark: "bg-[#873D00] text-[#FAF4F0]",
        danger: "bg-[#D90B0B] text-[#FAF4F0]",
        dangerMedium: "bg-[#F6584B] text-[#FAF4F0]",
        dangerDark: "bg-[#9E0000] text-[#FAF4F0]",
        
        // Disabled state
        disabled: "bg-[#E5E1DF] text-[#A09D9B]",
        
        // Default variants for backward compatibility
        default: "bg-[#E3FF6A] text-[#242424]",
        secondary: "bg-[#E5E1DF] text-[#242424]",
        destructive: "bg-[#D90B0B] text-[#FAF4F0]",
        outline: "border border-[#E5E1DF] text-[#242424] hover:bg-[#F7F4F2]",
      },
      size: {
        default: "px-1.5 py-0.5 text-xs font-medium leading-[15.6px]",
        sm: "px-1 py-0.5 text-[10px] font-medium",
        lg: "px-2 py-1 text-sm font-medium",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  disabled?: boolean
  asChild?: boolean
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, disabled, asChild = false, ...props }, ref) => {
    // Apply disabled styling if disabled prop is true
    const effectiveVariant = disabled ? "disabled" : variant
    
    const Comp = asChild ? Slot : "div"
    
    return (
      <Comp
        ref={ref}
        data-slot="badge"
        className={cn(
          badgeVariants({ variant: effectiveVariant, size }),
          disabled && "opacity-80",
          className
        )}
        {...(props as any)}
      />
    )
  }
)
Badge.displayName = "Badge"

export { Badge, badgeVariants }
